import type { Game } from 'phaser';
import { resolveQuerySelector, type QuerySelector } from './dom';

export type ActiveModal = 'none' | 'result' | 'continue' | 'consent' | 'ad';

export interface ModalTransitionOptions {
  readonly focus?: boolean;
}

export type ModalPanel = 'result' | 'continue' | 'consent';
export type ModalFocusTarget = ModalPanel | 'game';

export type ModalEffect =
  | { readonly type: 'set-state'; readonly modal: ActiveModal }
  | {
    readonly type: 'set-panel-visibility';
    readonly panel: ModalPanel;
    readonly visible: boolean;
  }
  | { readonly type: 'result-hidden' }
  | { readonly type: 'result-shown' }
  | { readonly type: 'time-pause' }
  | { readonly type: 'game-sleep' }
  | { readonly type: 'game-wake' }
  | { readonly type: 'time-resume' }
  | { readonly type: 'sync' }
  | { readonly type: 'focus'; readonly target: ModalFocusTarget };

const timedModals: ReadonlySet<ActiveModal> = new Set(['continue', 'consent', 'ad']);

export function planModalTransition(
  previous: ActiveModal,
  next: ActiveModal,
  options: ModalTransitionOptions = {},
): ModalEffect[] {
  if (previous === next) {
    return [];
  }

  const effects: ModalEffect[] = [
    { type: 'set-state', modal: next },
    { type: 'set-panel-visibility', panel: 'result', visible: next === 'result' },
    { type: 'set-panel-visibility', panel: 'continue', visible: next === 'continue' },
    { type: 'set-panel-visibility', panel: 'consent', visible: next === 'consent' },
  ];

  if (previous === 'result') {
    effects.push({ type: 'result-hidden' });
  }
  if (next === 'result') {
    effects.push({ type: 'result-shown' });
  }

  const wasTimed = timedModals.has(previous);
  const willBeTimed = timedModals.has(next);
  if (!wasTimed && willBeTimed) {
    effects.push({ type: 'time-pause' }, { type: 'game-sleep' });
  } else if (wasTimed && !willBeTimed) {
    effects.push({ type: 'game-wake' }, { type: 'time-resume' });
  }

  effects.push({ type: 'sync' });
  if (options.focus === false) {
    return effects;
  }

  const focusTarget: ModalFocusTarget | undefined = next === 'none'
    ? 'game'
    : next === 'ad' ? undefined : next;
  if (focusTarget !== undefined) {
    effects.push({ type: 'focus', target: focusTarget });
  }
  return effects;
}

export interface ModalsDeps {
  readonly getGame: () => Game | null;
  readonly onResultShown: () => void;
  readonly onResultHidden: () => void;
  readonly emitTimePause: () => void;
  readonly emitTimeResume: () => void;
  readonly sync: () => void;
  readonly focusContinueHeading: () => void;
  readonly isContinueHeadingFocused: () => boolean;
  readonly focusConsentHeading: () => void;
  readonly isConsentHeadingFocused: () => boolean;
  readonly querySelector?: QuerySelector;
}

export interface ModalsApi {
  readonly active: ActiveModal;
  getFallDistance(): number;
  setActive(next: ActiveModal, options?: ModalTransitionOptions): void;
  destroy(): void;
}

export function createModals(deps: ModalsDeps): ModalsApi {
  const querySelector = resolveQuerySelector(deps.querySelector);
  const gameFrame = querySelector<HTMLElement>('.game-frame');
  const completionPanel = querySelector<HTMLElement>('#completion-panel');
  const resultHeading = querySelector<HTMLElement>('#result-heading');
  const continueDialog = querySelector<HTMLElement>('#continue-dialog');
  const consentDialog = querySelector<HTMLElement>('#ad-consent-dialog');

  const panels: Readonly<Record<ModalPanel, HTMLElement | null>> = {
    result: completionPanel,
    continue: continueDialog,
    consent: consentDialog,
  };
  const focusTargets: Readonly<Record<ModalFocusTarget, () => void>> = {
    result: () => resultHeading?.focus({ preventScroll: true }),
    continue: deps.focusContinueHeading,
    consent: deps.focusConsentHeading,
    game: () => gameFrame?.focus({ preventScroll: true }),
  };
  let active: ActiveModal = 'none';

  const installFocusTrap = (
    dialog: HTMLElement | null,
    isInitialFocus: () => boolean,
  ): (() => void) => {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== 'Tab' || dialog === null) {
        return;
      }
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) {
        event.preventDefault();
        return;
      }
      const current = document.activeElement;
      if (event.shiftKey && (current === first || isInitialFocus())) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener('keydown', handler);
    return () => dialog?.removeEventListener('keydown', handler);
  };

  const removeFocusTraps = [
    installFocusTrap(completionPanel, () => document.activeElement === resultHeading),
    installFocusTrap(continueDialog, deps.isContinueHeadingFocused),
    installFocusTrap(consentDialog, deps.isConsentHeadingFocused),
  ];

  const applyEffect = (effect: ModalEffect): void => {
    switch (effect.type) {
      case 'set-state':
        active = effect.modal;
        break;
      case 'set-panel-visibility': {
        const panel = panels[effect.panel];
        if (panel !== null) {
          panel.hidden = !effect.visible;
        }
        break;
      }
      case 'result-hidden':
        deps.onResultHidden();
        break;
      case 'result-shown':
        deps.onResultShown();
        break;
      case 'time-pause':
        deps.emitTimePause();
        break;
      case 'game-sleep':
        deps.getGame()?.loop.sleep();
        break;
      case 'game-wake':
        deps.getGame()?.loop.wake();
        break;
      case 'time-resume':
        deps.emitTimeResume();
        break;
      case 'sync':
        deps.sync();
        break;
      case 'focus':
        focusTargets[effect.target]();
        break;
    }
  };

  return {
    get active() {
      return active;
    },
    getFallDistance: () => (completionPanel?.clientHeight ?? 420) + 60,
    setActive: (next, options) => {
      planModalTransition(active, next, options).forEach(applyEffect);
    },
    destroy: () => removeFocusTraps.forEach((remove) => remove()),
  };
}
